import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Keys, locale } from 'src/i18n';
import { defaultLeaderboardColumns } from './constant';
import { PerformanceState } from './types';

const initialState: PerformanceState = {
  status: {},
  snapshot: [],
  leaderboards: [],
  keyMetrics: {},
  rankings: {
    data: [],
    individual: {
      points: {
        value: 0,
        name: locale(Keys.MY_POINTS),
      },
      rank: {
        value: 0,
        name: locale(Keys.MY_RANKING),
      },
    },
    table: {
      title: locale(Keys.TOP_SCORERS),
      columns: defaultLeaderboardColumns,
    },
    listViewOptions: [],
  },
  keyMetricsDetails: {
    data: null,
    teamBreakdown: {
      data: [],
      columns: [],
    },
    breakdownGraph: null,
    trendGraph: null,
  },
};

export const performanceSlice = createSlice({
  name: 'performanceDashboard',
  initialState,
  reducers: {
    setStatus(
      state: PerformanceState,
      action: PayloadAction<PerformanceState['status']>,
    ) {
      state.status = {
        ...state.status,
        ...action.payload,
      };
    },
    setPerformanceSnapshot(
      state: PerformanceState,
      action: PayloadAction<PerformanceState['snapshot']>,
    ) {
      state.snapshot = action.payload;
    },
    setLeaderboards(
      state: PerformanceState,
      action: PayloadAction<PerformanceState['leaderboards']>,
    ) {
      state.leaderboards = action.payload;
    },
    setKeyMetrics(state: PerformanceState, action: PayloadAction<any>) {
      state.keyMetrics = action.payload;
    },
    setKeyMetricsMetadata(state: PerformanceState, action: PayloadAction<any>) {
      state.keyMetrics.metadata = action.payload;
    },
    setRankings(
      state: PerformanceState,
      action: PayloadAction<PerformanceState['rankings']>,
    ) {
      state.rankings = action.payload;
    },
    setKeyMetricsDetails(
      state: PerformanceState,
      action: PayloadAction<PerformanceState['keyMetricsDetails']['data']>,
    ) {
      state.keyMetricsDetails.data = action.payload;
    },
    setKeyMetricsTeamBreakdown(
      state: PerformanceState,
      action: PayloadAction<
        PerformanceState['keyMetricsDetails']['teamBreakdown']
      >,
    ) {
      state.keyMetricsDetails.teamBreakdown = action.payload;
    },
    setKeyMetricsBreakdownGraph(
      state: PerformanceState,
      action: PayloadAction<
        PerformanceState['keyMetricsDetails']['breakdownGraph']
      >,
    ) {
      state.keyMetricsDetails.breakdownGraph = action.payload;
    },
    setKeyMetricsTrendGraph(
      state: PerformanceState,
      action: PayloadAction<
        PerformanceState['keyMetricsDetails']['trendGraph']
      >,
    ) {
      state.keyMetricsDetails.trendGraph = action.payload;
    },
    clearPerformanceState() {
      return initialState;
    },
  },
});

export const {
  setStatus,
  setLeaderboards,
  setRankings,
  setKeyMetrics,
  setKeyMetricsMetadata,
  setKeyMetricsDetails,
  setKeyMetricsBreakdownGraph,
  setKeyMetricsTrendGraph,
  setKeyMetricsTeamBreakdown,
  clearPerformanceState,
  setPerformanceSnapshot,
} = performanceSlice.actions;

const reducer = { performanceDashboard: performanceSlice.reducer };

export default reducer;
